import type { DomainEventDescriptor } from '@heidi/runtime/events';

/**
 * Session's domain-event descriptors (arch §5.2 Aperture 2).
 *
 * Inert literals: the `DomainEventDescriptor` import is type-only, so
 * importing this module executes nothing and the contracts door stays
 * side-effect free.
 *
 * **Session emits these; nobody else may** — restated here rather than left to
 * `emitDomainEvent`'s JSDoc because a foreign domain tempted to emit one
 * arrives at this file, not at the runtime package. Desktop's bulk sync
 * creating sessions emits desktop's own event, not session's.
 *
 * The full rules — owner-only emission, loss-tolerant delivery, subscriptions
 * as declared DAG edges — live with the mechanism in `@heidi/runtime/events`.
 * Payloads carry identity only, so a subscriber refetches rather than treating
 * the payload as truth.
 *
 * Emit call sites land with the family that owns the mutation, wherever those
 * mutations end up riding (tasks.md D3/D6).
 */

/**
 * A session was created. Singular: each emission announces one session.
 *
 * Emit-site realities a subscriber MUST tolerate (landed with D3):
 * - `useMutateCreateNewSession` and `useMutateCreateNewSessionForOtherUser`
 *   both emit (creating on behalf of a teammate still announces — session
 *   owns the mutation, so owner-only emission holds).
 * - The BULK paths do not emit today: schedule-upload bulk creation
 *   (`postBulkCreateSessions*`) and offline-local session creation
 *   (`useMutateCreateOfflineSession`) announce nothing — their emits land
 *   when those mutations move. Subscribers needing bulk/offline coverage
 *   must not rely on this event alone.
 */
export const sessionCreatedEvent = {
  type: 'session.created',
} satisfies DomainEventDescriptor<{ sessionId: string }>;

/**
 * One or more sessions were deleted.
 *
 * Plural because deletion genuinely has a bulk form (`deleteSessionByIds`, and
 * the delete-all path), and one event per batch beats N events a subscriber
 * would only collapse into a single invalidation anyway.
 *
 * **`sessionIds` is advisory — best-effort, possibly partial or empty.**
 * Emit-site realities a subscriber MUST tolerate (treat the event as "some
 * sessions were deleted; refetch what you care about", never as an exact
 * inventory):
 * - delete-by-ids announces the input ids plus resolved server aliases;
 *   offline-LOCAL recording ids (no server identity) are deleted but not
 *   announced.
 * - delete-all announces the ids from its pre-delete cache snapshot — the
 *   cached pages only (under the v3 list it unions every cached paged query,
 *   deduped). Sessions never fetched into the cache are not announced, so
 *   the array can still be partial or empty.
 * - delete-empty does not emit at all: its response carries only a count.
 */
export const sessionDeletedEvent = {
  type: 'session.deleted',
} satisfies DomainEventDescriptor<{ sessionIds: string[] }>;
